import { useState, useEffect, useCallback, useRef } from "react";
import { X } from "lucide-react";
import { useTeamPresence } from "./TeamPresenceContext";

/**
 * TeamToastContainer — Renders toast notifications for incoming team messages
 * when the user is not viewing that conversation.
 *
 * Mount this once at the app level (inside TeamPresenceProvider).
 * Respects muted conversations.
 */

const TOAST_DURATION = 4000;
const MAX_TOASTS = 3;

const TeamToastContainer = () => {
  const { mutedConversations } = useTeamPresence();
  const [toasts, setToasts] = useState([]);
  const mutedRef = useRef(mutedConversations);
  mutedRef.current = mutedConversations;

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const msg = e.detail;
      if (!msg || !msg.team_id) return;

      // Don't toast for system messages or own messages
      if (msg.type === "system") return;

      // Check muted
      if (mutedRef.current.includes(msg.team_id)) return;

      const id = msg.message_id || Date.now().toString();

      // Build display content based on message type
      let displayContent = (msg.content || "").slice(0, 80);

      if (msg.type === "share_invite" && msg.share_meta) {
        const typeName = (msg.share_meta.content_type || "")
          .replace("custom_", "")
          .replace(/_/g, " ");
        const itemName =
          msg.share_meta.preview?.item_name ||
          msg.share_meta.preview?.company_name ||
          "";
        displayContent = itemName
          ? `Shared a ${typeName}: ${itemName}`
          : `Shared a ${typeName}`;
      } else if (msg.type === "shared_article" && msg.article_meta) {
        const articleTitle =
          msg.article_meta.article_snapshot?.title || "an article";
        displayContent = `Shared an article: ${articleTitle}`;
      }

      const toast = {
        id,
        senderName: msg.sender_name || "Someone",
        content: displayContent,
        teamId: msg.team_id,
        timestamp: Date.now(),
      };

      setToasts((prev) => {
        const next = [...prev, toast];
        // Cap at MAX_TOASTS
        if (next.length > MAX_TOASTS) return next.slice(-MAX_TOASTS);
        return next;
      });

      // Auto-remove after duration
      setTimeout(() => removeToast(id), TOAST_DURATION);
    };

    // Listen for the unread-toast event (fired by context when msg is for non-active conversation)
    window.addEventListener("team:toast-message", handler);
    return () => window.removeEventListener("team:toast-message", handler);
  }, [removeToast]);

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-12 right-4 z-50 flex flex-col gap-y-2 pointer-events-none">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className="pointer-events-auto w-[320px] bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg shadow-lg px-4 py-3 flex items-start gap-x-3 animate-slide-in"
          style={{
            animation: "toast-in 200ms ease-out",
          }}
        >
          {/* Sender initial */}
          <span className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-400 to-blue-500 flex items-center justify-center text-[13px] font-medium text-white shrink-0">
            {(toast.senderName[0] || "?").toUpperCase()}
          </span>

          <div className="flex-1 min-w-0">
            <p className="text-[12px] font-semibold text-zinc-800 dark:text-zinc-200 truncate">
              {toast.senderName}
            </p>
            <p className="text-[12px] text-zinc-500 dark:text-zinc-400 truncate">
              {toast.content}
            </p>
          </div>

          <button
            onClick={() => removeToast(toast.id)}
            className="p-0.5 rounded hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors shrink-0"
          >
            <X className="h-3.5 w-3.5 text-zinc-400" strokeWidth={2} />
          </button>
        </div>
      ))}

      {/* Inject animation keyframes */}
      <style>{`
        @keyframes toast-in {
          from { opacity: 0; transform: translateX(100px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};

export default TeamToastContainer;
